import { useProtectedRoute } from "~/composables/useProtectedRoute";
import type {
  ApiEndpoint,
  ApiEndpointBlobOutput,
  ApiEndpointMethod,
  ApiEndpointOutput,
  ApiEndpointResponse,
  ApiInvokeOptions,
} from "@asap/shared/clients/api";
import { ApiClient } from "@asap/shared/clients/api";

export const useApi = () => {
  const config = useRuntimeConfig();
  const { checkTokens, refreshTokens } = useProtectedRoute();

  const loading = ref(false);

  const getAuthenticatedClient = async (): Promise<ApiClient> => {
    loading.value = true;
    if (!checkTokens()) await refreshTokens();
    const accessToken = useCookie("sb-access-token").value;
    if (!accessToken) {
      loading.value = false;
      throw new Error("Missing access token");
    }
    return new ApiClient({ apiUrl: config.public.API_URL, accessToken, logger: console });
  };

  const invoke = async <
    Path extends ApiEndpoint["path"],
    Method extends ApiEndpoint["method"] = ApiEndpointMethod<Path>,
  >(
    options: ApiInvokeOptions<Path, Method>
  ): Promise<ApiEndpointOutput<Path, Method, ApiEndpointResponse<Path, Method>>> => {
    const apiClient = await getAuthenticatedClient();
    try {
      return await apiClient.invoke(options);
    } finally {
      loading.value = false;
    }
  };

  const invokeBlob = async <
    Path extends ApiEndpoint["path"],
    Method extends ApiEndpoint["method"] = ApiEndpointMethod<Path>,
  >(
    options: Omit<ApiInvokeOptions<Path, Method>, "maybeNull" | "responseType">
  ): Promise<ApiEndpointBlobOutput> => {
    const apiClient = await getAuthenticatedClient();
    try {
      const response = await apiClient.invoke({
        ...options,
        responseType: "blob",
      } as ApiInvokeOptions<Path, Method>);
      return response as ApiEndpointBlobOutput;
    } finally {
      loading.value = false;
    }
  };

  return { loading, invoke, invokeBlob };
};
